import type { ChatMessage } from '../types/chat';

/** Show a stamp when the thread has been idle this long. */
const TIME_GAP_MS = 4 * 60 * 1000;

function localeTag(locale: string): string {
  return locale.toLowerCase().startsWith('ms') ? 'ms-MY' : 'en-US';
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Centered time chip: first message in the thread, ≥4 minutes idle,
 * or first chat message after a session refresh.
 */
export function shouldShowMessageTime(
  current: ChatMessage,
  previous: ChatMessage | undefined,
): boolean {
  if (current.sender === 'divider' || current.sender === 'system') return false;
  if (current.createdAt == null) return false;
  if (!previous) return true;
  if (previous.sender === 'divider') return true;
  if (previous.createdAt == null) return true;
  return current.createdAt - previous.createdAt >= TIME_GAP_MS;
}

export function formatMessageTime(
  ts: number,
  locale: string,
  yesterdayLabel: string,
): string {
  const date = new Date(ts);
  const now = new Date();
  const tag = localeTag(locale);
  const time = new Intl.DateTimeFormat(tag, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

  if (startOfDay(date) === startOfDay(now)) return time;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (startOfDay(date) === startOfDay(yesterday)) {
    return `${yesterdayLabel}, ${time}`;
  }

  const sixDaysAgo = new Date(now);
  sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
  if (date.getTime() >= startOfDay(sixDaysAgo)) {
    const weekday = new Intl.DateTimeFormat(tag, { weekday: 'long' }).format(
      date,
    );
    return `${weekday}, ${time}`;
  }

  return new Intl.DateTimeFormat(tag, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}
